import { FormEvent, useEffect, useState } from 'react';
import ErrorBoundary from '../../../ErrorBoundary';
import { useStoredResult } from '../../../common/hooks/useStoredResult';
import { removeStorageKey, saveToStorage } from '../../../common/lib/storage';
import { ErrorTypes } from '../../../types/ErrorTypes';
import { FormError } from '../../../types/FormError';
import { LoadState } from '../../../types/LoadState';
import * as ForetagClient from '../../clients/foretagClient';
import { formErrorMap } from '../../lib/formErrorMap';
import { LedningDto } from '../../model/LedningDto';
import { ForetagTransactions } from '../../types/ForetagTransactions';
import { ForetagAdditionalInfoRow } from '../ForetagAdditionalInfoRow/ForetagAdditionalInfoRow';
import { Ledning } from './LedningSearchResult';

interface LedningInfoContainerProps {
  organisationsnummer: string;
  companyName: string;
  initialKundmarke: string;
  permissions: ForetagTransactions[];
  cacheKey: string;
}

export const LedningInfoContainer: React.FC<LedningInfoContainerProps> = ({
  organisationsnummer,
  companyName,
  initialKundmarke,
  permissions = [],
  cacheKey,
}) => {
  const [error, setError] = useState<FormError>();
  const [kundmarke, setKundmarke] = useState('');

  const { loadState, searchResult, setLoadState, setSearchResult } =
    useStoredResult<LedningDto>(cacheKey, organisationsnummer);

  useEffect(() => setKundmarke(initialKundmarke), [initialKundmarke]);

  const onBuy = async (
    e: FormEvent<HTMLFormElement>,
    organisationsnummer: string
  ) => {
    e.preventDefault();
    setLoadState(LoadState.Loading);
    const response = await ForetagClient.getLedning(
      organisationsnummer,
      kundmarke
    );
    if (response.success) {
      saveToStorage<LedningDto>(cacheKey, response.data);
      setLoadState(LoadState.Success);
      setSearchResult(response.data);
    } else {
      setLoadState(LoadState.Error);
      setError(
        {
          403: formErrorMap[ErrorTypes.Forbidden],
          404: formErrorMap[ErrorTypes.ForetagNotFound],
        }[response.statusCode] || formErrorMap[ErrorTypes.UnknownError]
      );
    }
  };

  const ledning = searchResult?.omfragadlista?.[0];

  return (
    <ErrorBoundary onError={() => removeStorageKey(cacheKey)}>
      <form onSubmit={(e) => onBuy(e, organisationsnummer)}>
        <ForetagAdditionalInfoRow
          heading="Ledning och firmateckning"
          buyDisabled={!permissions.includes(ForetagTransactions.IPM_SYNA_LEFI)}
          loadState={loadState}
          error={error}
          organisationsnummer={organisationsnummer}
          companyName={companyName}
          kundmarke={kundmarke}
          setKundmarke={setKundmarke}
          sourceName="Syna kredit- och affärsinformation"
        >
          {ledning && <Ledning ledning={ledning} />}
        </ForetagAdditionalInfoRow>
      </form>
    </ErrorBoundary>
  );
};
